import type { CaseDetailDto } from '../../api/types';
import { HELP } from '../../lib/help';
import { HelpTip } from '../HelpTip';
import { MoreMenu } from './MoreMenu';

export interface ActionBarProps {
    detail: CaseDetailDto;
    busy: boolean;
    onApprove: () => void;
    onSend: () => void;
    onExtend: () => void;
    onExtendDecide: (approved: boolean) => void;
    onAssign: () => void;
    onEscalate: () => void;
    onComment: () => void;
    onReopen: () => void;
    onChangeDepartment: (departmentId: string | null, name: string) => void;
}

/**
 * Панель действий над делом (ТЗ §8.5, права — ТЗ §2). На виду — частые действия (согласовать, отправить, решение по
 * продлению, переназначить, комментарий), редкие — в меню «Ещё» (продлить срок, эскалация, переоткрыть, сменить
 * отделение). Показ каждой кнопки решает
 * серверный блок `permissions`: клиент только прячет, право проверяет бэкенд.
 * У закрытого дела прав не остаётся, контейнер пустеет и его убирает `.modal-actions:empty`.
 */
export function ActionBar({
    detail,
    busy,
    onApprove,
    onSend,
    onExtend,
    onExtendDecide,
    onAssign,
    onEscalate,
    onComment,
    onReopen,
    onChangeDepartment,
}: ActionBarProps) {
    const { permissions: can, case: item, departments } = detail;
    const extendPending = item.extended && item.extend_approved === null;
    // Редкие действия — в меню «Ещё» (доработка шага 11), частые остаются на панели; права — те же `permissions`.
    const hasMore = (can.extend_request && !item.extended) || (can.escalate && !item.escalated) || can.reopen || can.change_department;

    return (
        <div className="modal-actions">
            {can.approve ? (
                <button type="button" className="act-btn primary" disabled={busy} onClick={onApprove}>
                    ✓ Согласовать ответ
                </button>
            ) : null}

            {can.send ? (
                <button type="button" className="act-btn primary" disabled={busy} onClick={onSend}>
                    📨 Отправить заявителю
                </button>
            ) : null}

            {can.extend_decide && extendPending ? (
                <button type="button" className="act-btn warn" disabled={busy} onClick={() => onExtendDecide(true)}>
                    ⏳ Утвердить продление
                </button>
            ) : null}

            {can.extend_decide && extendPending ? (
                <button type="button" className="act-btn" disabled={busy} onClick={() => onExtendDecide(false)}>
                    Отклонить продление
                </button>
            ) : null}

            {can.assign ? (
                <button type="button" className="act-btn" disabled={busy} onClick={onAssign}>
                    👤 Переназначить исполнителя
                </button>
            ) : null}

            {can.comment ? (
                <button type="button" className="act-btn" onClick={onComment}>
                    💬 Комментарий
                </button>
            ) : null}

            {hasMore ? (
                <MoreMenu>
                    {can.extend_request && !item.extended ? (
                        <span className="act-with-help">
                            <button type="button" className="act-btn warn" disabled={busy} onClick={onExtend}>
                                ⏳ Продлить срок на 30 дней
                            </button>
                            <HelpTip text={HELP.extend} label="продление срока" />
                        </span>
                    ) : null}

                    {can.escalate && !item.escalated ? (
                        <span className="act-with-help">
                            <button type="button" className="act-btn warn" disabled={busy} onClick={onEscalate}>
                                🛡 Запросить эскалацию
                            </button>
                            <HelpTip text={HELP.escalate} label="эскалация" />
                        </span>
                    ) : null}

                    {can.reopen ? (
                        <button type="button" className="act-btn warn" disabled={busy} onClick={onReopen}>
                            ↻ Переоткрыть дело
                        </button>
                    ) : null}

                    {can.change_department ? (
                        <select
                            className="act-select"
                            aria-label="Сменить отделение"
                            disabled={busy}
                            value=""
                            onChange={(event) => {
                                const value = event.target.value;
                                event.target.value = '';
                                if (!value) return;
                                if (value === 'none') {
                                    onChangeDepartment(null, '');
                                    return;
                                }
                                const dept = departments.find((d) => d.id === value);
                                onChangeDepartment(value, dept ? dept.name : '');
                            }}
                        >
                            <option value="">Сменить отделение…</option>
                            <option value="none">— Без отделения —</option>
                            {departments.map((dept) => (
                                <option key={dept.id} value={dept.id} disabled={dept.id === item.department_id}>
                                    {(dept.icon ? dept.icon + ' ' : '') + dept.name}
                                </option>
                            ))}
                        </select>
                    ) : null}
                </MoreMenu>
            ) : null}
        </div>
    );
}

export default ActionBar;
